import { Input } from "@components/forms/input";
import { Masks } from "@hooks/functions/Masks/Masks";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useEffect, useState } from "react";
import { FiPercent, FiDollarSign, FiInfo, FiTrendingUp } from "react-icons/fi";
import Swal from "sweetalert2";

interface IProps {
  raffleKey: string;
  customerId: number;
  isOpen: boolean;
  onClose: (updated?: boolean) => void;
}

// Customer Request to be Affiliate in Campaign
const masks = new Masks();
export function ModalRaffleAffiliateRequest({
  raffleKey,
  isOpen,
  onClose,
}: IProps) {
  // Stores and states
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const [animate, setAnimate] = useState(false);
  const [percent, setPercent] = useState<number | string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate example commission based on ticket price
  const exampleTicketPrice = 10; // Example: 10 per ticket
  const calculatedCommission =
    percent && Number(percent) > 0
      ? (exampleTicketPrice * Number(percent)) / 100
      : 0;

  // Validate percentage input
  const isValidPercent = (): boolean => {
    const numPercent = Number(percent);
    if (percent === "" || isNaN(numPercent)) {
      pushToastify({
        message: "Por favor, informe o percentual de comissão desejado",
        type: "warning",
        theme: "dark",
      });
      return false;
    }
    if (numPercent <= 0) {
      pushToastify({
        message: "O percentual deve ser maior que zero",
        type: "warning",
        theme: "dark",
      });
      return false;
    }
    if (numPercent > 100) {
      pushToastify({
        message: "O percentual não pode ser maior que 100%",
        type: "warning",
        theme: "dark",
      });
      return false;
    }
    return true;
  };

  // Save changes with confirmation
  const handleSaveChanges = async () => {
    if (!isValidPercent()) return;

    Swal.fire({
      title: "Enviar Solicitação?",
      text: "Deseja enviar esta proposta de afiliação?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, salvar!",
      cancelButtonText: "Cancelar",
    }).then(async (result: { isConfirmed: boolean }) => {
      if (result.isConfirmed) {
        setIsSubmitting(true);
        try {
          const data = { percent: Number(percent), key: raffleKey };
          const response = await api.post(`/raffle/affiliate`, data);

          pushToastify({
            message: "Solicitação enviada com sucesso!",
            type: "success",
            theme: "dark",
          });

          handleCancel(true);
        } catch (err: any) {
          pushToastify({
            message:
              err?.response?.data.message ?? "Erro ao enviar a solicitação!",
            type: "error",
            theme: "dark",
          });
        } finally {
          setIsSubmitting(false);
        }
      }
    });
  };

  // Close modal
  function handleCancel(update?: boolean) {
    if (isSubmitting) return;
    setAnimate(false);
    setTimeout(() => {
      onClose(update);
      setPercent(""); // Reset form
    }, 300);
  }

  // Handle escape key
  function handleEscape(evt: KeyboardEvent) {
    if (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27) {
      handleCancel();
    }
  }

  // Handle enter key to submit
  function handleEnter(evt: KeyboardEvent) {
    if (evt.key === "Enter" && !isSubmitting) {
      evt.preventDefault();
      handleSaveChanges();
    }
  }

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("keydown", handleEnter);
      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.removeEventListener("keydown", handleEnter);
      };
    } else {
      setAnimate(false);
    }
  }, [isOpen, isSubmitting]);

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-labelledby="affiliateRequestModalLabel"
        aria-hidden={!isOpen}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="affiliateRequestModalLabel">
                Solicitação de Afiliação
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  handleCancel(false);
                }}
                disabled={isSubmitting}
              ></button>
            </div>

            <div className="modal-body">
              {/* Info banner */}
              <div className="alert alert-info d-flex align-items-start mb-4">
                <FiInfo className="me-2 mt-1 flex-shrink-0" size={20} />
                <div className="small">
                  <strong>Como funciona:</strong> Como afiliado, você receberá
                  uma comissão sobre cada ticket vendido através do seu link de
                  divulgação. Informe abaixo o percentual de comissão que deseja
                  receber.
                </div>
              </div>

              {/* Commission percentage input */}
              <div className="mb-3">
                <Input
                  animated
                  label="Percentual de Comissão (%)"
                  placeholder="Ex: 10"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={percent}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPercent(value === "" ? "" : Number(value));
                  }}
                  disabled={isSubmitting}
                />
                <small className="text-muted d-block mt-1">
                  <FiPercent size={12} className="me-1" />
                  Informe a porcentagem de comissão que deseja receber por cada
                  ticket vendido
                </small>
              </div>

              {/* Commission preview card - Professional style */}
              {percent && Number(percent) > 0 && (
                <div className="card border shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <FiTrendingUp className="text-primary me-2" size={20} />
                        <h6 className="card-title mb-0 fw-semibold">
                          Exemplo de Projeção
                        </h6>
                      </div>
                      <span className="badge bg-secondary bg-opacity-25 text-white py-2 px-3">
                        Simulação
                      </span>
                    </div>

                    <div className="alert alert-light border mb-3 py-2 px-3">
                      <small className="d-flex align-items-start text-muted mb-0">
                        <FiInfo className="me-2 mt-1 flex-shrink-0" size={14} />
                        <span>
                          Esta é apenas uma{" "}
                          <strong>simulação ilustrativa</strong> baseada em um
                          bilhete hipotético de{" "}
                          <strong>
                            {masks.brlCurrency(exampleTicketPrice)}
                          </strong>
                          . Os valores reais variam conforme o preço do bilhete
                          de cada campanha.
                        </span>
                      </small>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-sm table-borderless mb-0">
                        <thead>
                          <tr className="border-bottom">
                            <th className="text-muted small fw-normal pb-2">
                              Bilhetes Vendidos
                            </th>
                            <th className="text-end text-muted small fw-normal pb-2">
                              Comissão Estimada
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="py-2">
                              <span className="text-muted">1 bilhete</span>
                              <small
                                className="text-muted d-block"
                                style={{ fontSize: "0.75rem" }}
                              >
                                (exemplo:{" "}
                                {masks.brlCurrency(exampleTicketPrice)})
                              </small>
                            </td>
                            <td className="text-end text-muted py-2">
                              <span>
                                {masks.brlCurrency(calculatedCommission)}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted py-2">50 bilhetes</td>
                            <td className="text-end text-muted py-2">
                              <span>
                                {masks.brlCurrency(calculatedCommission * 50)}
                              </span>
                            </td>
                          </tr>
                          <tr className="bg-light bg-opacity-50">
                            <td className="text-muted py-2">100 bilhetes</td>
                            <td className="text-end py-2">
                              <span className="text-primary">
                                {masks.brlCurrency(calculatedCommission * 100)}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="alert alert-warning mt-3 mb-0 small d-flex align-items-start">
                      <FiInfo className="me-2 mt-1 flex-shrink-0" size={16} />
                      <div>
                        <strong>Importante:</strong> O organizador da campanha
                        analisará sua solicitação e poderá aprová-la, recusá-la
                        ou propor um percentual diferente. A comissão é
                        calculada sobre o <strong>valor real</strong> de cada
                        bilhete da campanha específica.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-success rounded"
                onClick={handleSaveChanges}
                disabled={isSubmitting || !percent}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Enviando...
                  </>
                ) : (
                  "Enviar Solicitação"
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary rounded"
                onClick={() => {
                  handleCancel(false);
                }}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
      ></div>
    </>
  );
}
